import Api from '@/services/Api';
import { getClientEnv } from './clientEnv';

export default {
  namespaced: true,
  state: {
    presupuestos: [], // Almacenará los presupuestos obtenidos del servicio
    detallesPresupuestos: [], // Almacenará las lineas de los detalles de los presupuestos
    detallesPresupuestosSummary: [], // Almacenará todo el objeto de los detalles de presupuestos
    detallesClientePresupuesto: [], // Almacenará el objeto de los detalles del cliente para el presupuesto que le pertenece
    detallesLineas: [], // Almacenará los detalles de las lineas de los presupuestos
    centroProduccion: [], // Almacenará las empresas para definir el centro de producción
    subFamilias: [], // Almacenará los PNTs y PTs de las subfamilias
    analisisTipoDescrip: [], // Almacenará los tipos de análisis para obtener la descripción
    detailsNoAnalyticsDescro: [],
    showDetailsPopup: false, // Controla la visibilidad del popup del detalles de los detalles del presupuesto
    showPtsPopup: false, // Controla la visibilidad del popup de los PTs
    popupTitle: '', // Almacena el titulo del popup
    divisa: '', // Almacena la divisa del presupuesto
    isQCA: false, // Controla si el presupuesto es QCA
    isQMT: false, // Controla si el presupuesto es QMT
    RespuestaAceptacion: [], // Almacena la respuesta de la aceptación del presupuesto (acumulativa)
    clientInfo: null,
    status: [],
  },
  actions: {
    async fetchPresupuestos(
      { commit, rootState },
      options = null
    ) {
      try {
        const normalizedOptions =
          options !== null &&
          typeof options === 'object' &&
          !Array.isArray(options)
            ? options
            : { estado: options };

        const {
          estado = null,
          isOnlineRegister = false,
        } = normalizedOptions;

        const xhr = await Api.getOfferList(
          rootState.contact.company,
          estado,
          isOnlineRegister
        );
        const response = JSON.parse(xhr.response);
        commit('setPresupuestos', response.presupuestos);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // ACEPTAR UNA SOLICITUD, acumula cada respuesta
    async aceptarPresupuesto({ commit }, data) {
      try {
        const clientInfo = await getClientEnv();
        commit('setClientInfo', clientInfo);

        // Campos para el API
        data.IP = clientInfo?.ip ?? null;
        data.Navegador =
          clientInfo?.browserLabel ??
          // Fallbacks por si algún navegador no expone UA-CH
          (clientInfo?.browserName
            ? `${clientInfo.browserName}${
                clientInfo?.browserVersion
                  ? ` ${clientInfo.browserVersion}`
                  : ''
              }`
            : clientInfo?.ua ?? null);

        const xhr = await Api.acceptOffers(data);
        const response = JSON.parse(xhr.response);

        // ACUMULAR en el array en vez de sobrescribir
        commit('pushRespuestaAceptacion', response);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },

    // ACEPTAR EN LOTE VARIAS SOLICITUDES, acumula todas las respuestas exitosas
    async aceptarPresupuestos(
      { commit, state },
      dataItems /* Array<DatosParaAPI> */
    ) {
      try {
        const clientInfo = await getClientEnv();
        commit('setClientInfo', clientInfo);

        const enrich = (data) => ({
          ...data,
          IP: clientInfo?.ip ?? null,
          Navegador:
            clientInfo?.browserLabel ??
            (clientInfo?.browserName
              ? `${clientInfo.browserName}${
                  clientInfo?.browserVersion
                    ? ` ${clientInfo.browserVersion}`
                    : ''
                }`
              : clientInfo?.ua ?? null),
        });

        const requests = dataItems.map((d) =>
          Api.acceptOffers(enrich(d)).then((xhr) =>
            JSON.parse(xhr.response)
          )
        );

        const settled = await Promise.allSettled(requests);

        const exitos = settled
          .filter((r) => r.status === 'fulfilled')
          .map((r) => r.value);

        const errores = settled
          .filter((r) => r.status === 'rejected')
          .map((r) => r.reason);

        // Acumular lo nuevo con lo que ya hubiera en el state
        commit('setRespuestasAceptacion', [
          ...(state.RespuestaAceptacion || []),
          ...exitos,
        ]);

        // Devolver resumen por si el UI quiere mostrarlo
        return { exitos, errores };
      } catch (error) {
        return Promise.reject(error);
      }
    },

    async fetchDetallesPresupuestos(
      { commit, rootState },
      codigoPresupuesto
    ) {
      try {
        const codIdioma =
          rootState.user.authenticatedUser.language_code ||
          rootState.user.authenticatedUser.contacts[0].language;
        const xhr = await Api.getOfferDetails(
          codigoPresupuesto,
          codIdioma
        );
        if (codigoPresupuesto.substring(0, 3) === 'QCA') {
          commit('setisQCA', true);
        } else {
          commit('setisQCA', false);
        }

        if (codigoPresupuesto.substring(0, 3) === 'QMT') {
          commit('setisQMT', true);
        } else {
          commit('setisQMT', false);
        }

        const response = JSON.parse(xhr.response);
        commit('setDetallesPresupuestos', response.Lineas);
        commit('setDetallesPresupuestosSummary', response);
        commit('setDivisa', response.CodDivisa);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchDetallesPresupuestosSummary({ commit, rootState }) {
      try {
        const xhr = await Api.getOfferSummaryDetails(
          rootState.contact.company
        );
        const response = JSON.parse(xhr.response);
        commit('setDetallesClientePresupuesto', response);
        return Promise.resolve();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchDetallesLineas({ commit }, data) {
      try {
        const xhr = await Api.getLineDetails(data);
        const response = JSON.parse(xhr.response);
        commit('setDetallesLineas', response);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async fetchAnalisisTipo({ commit, rootState }, data) {
      try {
        data.codCliente =
          data.codCliente || rootState.contact.company;
        data.codIdioma =
          data.codIdioma ||
          rootState.user.authenticatedUser.language_code ||
          rootState.user.authenticatedUser.contacts[0].language;
        data.codPlanta =
          data.codPlanta ||
          data.CodPlantaCentroProduccionSeleccionada ||
          rootState.contact.company.substring(0, 2);
        const xhr = await Api.getAnalysisType(data);
        const response = JSON.parse(xhr.response);
        commit('setAnalisisTipoDescrip', response.analisisTipo);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async get_empresas_centro_produccion({ commit }) {
      try {
        const xhr = await Api.getProductionCenterCompanies();
        const response = JSON.parse(xhr.response);
        commit('setCentroProduccion', response);
        return Promise.resolve(response);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async formatStatusForSummary({ commit }, statusCode) {
      try {
        let estado;
        switch (statusCode) {
          case 1:
            estado = 'offerStatus.pending';
            break;
          case 2:
            estado = 'offerStatus.rejected';
            break;
          case 3:
            estado = 'offerStatus.accepted';
            break;
          case 4:
            estado = 'offerStatus.cancelled';
            break;
          default:
            estado = 'Estado desconocido';
            break;
        }
        commit('setStatus', estado);
        return Promise.resolve(estado);
      } catch (error) {
        return Promise.reject(error);
      }
    },
    setPresupuestos({ commit }, value) {
      commit('setPresupuestos', value);
    },
    setDetallesPresupuestos({ commit }, value) {
      commit('setDetallesPresupuestos', value);
    },
    setDetallesPresupuestosSummary({ commit }, value) {
      commit('setDetallesPresupuestosSummary', value);
    },
    setDetallesClientePresupuesto({ commit }, value) {
      commit('setDetallesClientePresupuesto', value);
    },
    setShowDetailsPopup({ commit }, value) {
      commit('setShowDetailsPopup', value);
    },
    setShowPtsPopup({ commit }, value) {
      commit('setShowPtsPopup', value);
    },
    setisQCA({ commit }, value) {
      commit('setisQCA', value);
    },
    setisQMT({ commit }, value) {
      commit('setisQMT', value);
    },
    setPopupTitle({ commit }, value) {
      commit('setPopupTitle', value);
    },
    setDetallesLineas({ commit }, value) {
      commit('setDetallesLineas', value);
    },
    setCentroProduccion({ commit }, value) {
      commit('setCentroProduccion', value);
    },
    setDivisa({ commit }, value) {
      commit('setDivisa', value);
    },
    setSubFamilias({ commit }, value) {
      commit('setSubFamilias', value);
    },
    setAnalisisTipoDescrip({ commit }, value) {
      commit('setAnalisisTipoDescrip', value);
    },
    setDetailsNoAnalytics({ commit }, value) {
      commit('setDetailsNoAnalytics', value);
    },
    // Mantengo esta acción por compatibilidad, pero OJO: sobrescribe el array.
    setRespuestaAceptacion({ commit }, value) {
      commit('setRespuestaAceptacion', value);
    },
    setClientInfo({ commit }, value) {
      commit('setClientInfo', value);
    },
    setStatus({ commit }, value) {
      commit('setStatus', value);
    },
    resetData({ commit }) {
      commit('resetData');
    },
  },
  mutations: {
    setPresupuestos(state, value) {
      state.presupuestos = value;
    },
    setDetallesPresupuestos(state, value) {
      state.detallesPresupuestos = value;
    },
    setDetallesPresupuestosSummary(state, value) {
      state.detallesPresupuestosSummary = value;
    },
    setDetallesClientePresupuesto(state, value) {
      state.detallesClientePresupuesto = value;
    },
    setShowDetailsPopup(state, value) {
      state.showDetailsPopup = value;
    },
    setShowPtsPopup(state, value) {
      state.showPtsPopup = value;
    },
    setisQCA(state, value) {
      state.isQCA = value;
    },
    setisQMT(state, value) {
      state.isQMT = value;
    },
    setPopupTitle(state, value) {
      state.popupTitle = value;
    },
    setDetallesLineas(state, value) {
      state.detallesLineas = value;
    },
    setCentroProduccion(state, value) {
      state.centroProduccion = value;
    },
    setSubFamilias(state, value) {
      state.subFamilias = value;
    },
    setAnalisisTipoDescrip(state, value) {
      state.analisisTipoDescrip = value;
    },
    setDivisa(state, value) {
      state.divisa = value;
    },
    setDetailsNoAnalytics(state, value) {
      state.detailsNoAnalyticsDescro = value;
    },
    // Sobrescribe todo el array (compatibilidad)
    setRespuestaAceptacion(state, value) {
      state.RespuestaAceptacion = value;
    },
    // NUEVO: push unitario para acumular
    pushRespuestaAceptacion(state, value) {
      state.RespuestaAceptacion.push(value);
    },
    // NUEVO: set masivo (útil para lote)
    setRespuestasAceptacion(state, values) {
      state.RespuestaAceptacion = values;
    },

    setClientInfo(state, value) {
      state.clientInfo = value;
    },
    setStatus(state, value) {
      state.status = value;
    },
    resetData(state) {
      state.presupuestos = [];
      state.detallesPresupuestos = [];
      state.detallesPresupuestosSummary = [];
      state.detallesClientePresupuesto = [];
      state.showDetailsPopup = false;
      state.showPtsPopup = false;
      state.isQCA = false;
      state.isQMT = false;
      state.popupTitle = '';
      state.detallesLineas = [];
      state.centroProduccion = [];
      state.subFamilias = [];
      state.analisisTipoDescrip = [];
      state.divisa = '';
      state.detailsNoAnalyticsDescro = [];
      state.RespuestaAceptacion = [];
      state.clientInfo = [];
      state.status = [];
    },
  },
};
